/**
 * SpeedTech Lights - Rebuy Widget Custom Integration
 * Handles:
 * - Dynamic price updates when accessories/features are checked
 * - Add accessories to cart with parent product
 */

(function() {
  'use strict';
  // deploy refresh 2026-07-22 (force GitHub-integration re-pull for PR #36)

  const RebuyCustom = {
    // Widget IDs
    ACCESSORIES_WIDGET_ID: '290618',
    ACCESSORIES_WIDGET_ID_2: '292456',
    BRACKETS_WIDGET_ID: '301541',
    UPGRADABLE_WIDGET_ID: '290682',

    // State
    selectedItems: new Map(),
    basePrice: 0,
    setupComplete: false,

    init: function() {
      // Wait for DOM and Rebuy widgets to load
      this.waitForWidgets();
    },

    waitForWidgets: function() {
      const checkWidgets = () => {
        const widgets = document.querySelectorAll('[data-rebuy-id]');
        if (widgets.length > 0 && !this.setupComplete) {
          this.setup();
        } else if (!this.setupComplete) {
          requestAnimationFrame(checkWidgets);
        }
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', checkWidgets);
      } else {
        // Check immediately and also after a delay for dynamic loading
        checkWidgets();
        setTimeout(checkWidgets, 1000);
        setTimeout(checkWidgets, 2000);
      }
    },

    setup: function() {
      if (this.setupComplete) return;
      this.setupComplete = true;

      this.cacheElements();
      this.getBasePrice();
      this.bindEvents();
      this.observeWidgets();
      this.observeContextualPrices();
    },

    cacheElements: function() {
      this.priceElement = document.querySelector('.price__regular .price-item--regular, .price__sale .price-item--sale, .price .money');
      // Find the product form more reliably - look for the form inside product-form custom element
      const productFormEl = document.querySelector('product-form');
      this.addToCartForm = productFormEl ? productFormEl.querySelector('form') : document.querySelector('form[action*="/cart/add"]');
      this.addToCartButton = document.querySelector('.product-form__submit');
    },

    getBasePrice: function() {
      if (this.priceElement) {
        const priceText = this.priceElement.textContent.trim();
        this.basePrice = this.parsePrice(priceText);
      }
    },

    parsePrice: function(priceString) {
      const cleaned = priceString.replace(/[^0-9.,]/g, '').replace(',', '');
      return parseFloat(cleaned) || 0;
    },

    formatPrice: function(price) {
      // Use Shopify's active currency for multi-currency support (Markets)
      const currency = window.Shopify?.currency?.active || 'USD';
      const locale = currency === 'USD' ? 'en-US' : undefined; // Let browser pick locale for non-USD
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency
        }).format(price);
      } catch (e) {
        // Never let a bad/unknown currency code throw and abort every widget's price patch.
        return '$' + (price || 0).toFixed(2);
      }
    },

    bindEvents: function() {
      // Use event delegation for Rebuy checkboxes
      document.addEventListener('change', (e) => {
        const checkbox = e.target.closest('.rebuy-checkbox, [data-rebuy-id] input[type="checkbox"]');
        if (checkbox) {
          this.handleCheckboxChange(checkbox);
        }
      });

      // Listen for variant changes
      document.addEventListener('change', (e) => {
        const select = e.target.closest('[data-rebuy-id] select');
        if (select) {
          this.handleVariantChange(select);
        }
      });

      // Listen for quantity changes to trigger price update
      document.addEventListener('change', (e) => {
        if (e.target.classList && e.target.classList.contains('quantity__input')) {
          this.triggerPriceUpdate();
        }
      });
      document.addEventListener('input', (e) => {
        if (e.target.classList && e.target.classList.contains('quantity__input')) {
          this.triggerPriceUpdate();
        }
      });
      document.addEventListener('click', (e) => {
        if (e.target.closest('.quantity__button')) {
          // Delay to let the quantity value update
          setTimeout(() => this.triggerPriceUpdate(), 50);
        }
      });

      // Intercept add to cart - listen to both click and form submit
      if (this.addToCartButton) {
        // Intercept click on the button
        this.addToCartButton.addEventListener('click', (e) => {
          if (this.selectedItems.size > 0 && !this.isAddingToCart) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.addAllToCart();
          }
        }, true); // Use capture phase to intercept before other handlers
      }

      // Also intercept the form submit event as backup
      if (this.addToCartForm) {
        this.addToCartForm.addEventListener('submit', (e) => {
          if (this.selectedItems.size > 0 && !this.isAddingToCart) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.addAllToCart();
          }
        }, true); // Use capture phase
      }
    },

    isAddingToCart: false,

    precheckDebounceTimer: null,

    // Trigger price update in main-product.liquid
    triggerPriceUpdate: function() {
      if (window.RebuyPriceUpdater && typeof window.RebuyPriceUpdater.update === 'function') {
        window.RebuyPriceUpdater.update();
      }
      // Also dispatch event for any other listeners
      document.dispatchEvent(new CustomEvent('rebuy:price-update'));
    },

    observeWidgets: function() {
      // Watch for widget content changes (for initializing items only, not precheck)
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        mutations.forEach((mutation) => {
          if (mutation.addedNodes.length) {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) {
                const items = node.querySelectorAll ? node.querySelectorAll('.rebuy-addon__item') : [];
                items.forEach(item => this.initializeItem(item));
                if (items.length > 0) shouldUpdate = true;
              }
            });
          }
          // Watch for class changes that indicate selection state
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            shouldUpdate = true;
          }
        });
        if (shouldUpdate) {
          setTimeout(() => this.triggerPriceUpdate(), 50);
        }
      });

      document.querySelectorAll('[data-rebuy-id]').forEach(widget => {
        observer.observe(widget, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        // Initialize existing items
        widget.querySelectorAll('.rebuy-addon__item').forEach(item => {
          this.initializeItem(item);
        });
      });

      // Wait for Rebuy to fully initialize before prechecking
      // Use a single consolidated approach
      let precheckScheduled = false;
      const schedulePrecheckOnce = () => {
        if (precheckScheduled) return;
        precheckScheduled = true;
        // Wait for Rebuy Vue to finish rendering
        setTimeout(() => this.initPrecheck(), 500);
      };

      document.addEventListener('rebuy:cart.ready', schedulePrecheckOnce);
      document.addEventListener('rebuy.ready', schedulePrecheckOnce);
      document.addEventListener('rebuy:ready', schedulePrecheckOnce);

      // Fallback: check after longer delays if Rebuy events don't fire
      // Include extra delays for widgets like brackets (301541) that may render later
      setTimeout(() => this.initPrecheck(), 2500);
      setTimeout(() => this.initPrecheck(), 5000);
      setTimeout(() => this.initPrecheck(), 7500);

      // Back button: a page restored from the bfcache keeps its post-add (unchecked) DOM and no
      // script re-runs, so the pre-check would be lost. Re-apply the defaults on bfcache restore.
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) this.reapplyPrecheck();
      });
    },

    // Precheck tracking
    precheckApplied: false,
    precheckAttempts: 0,
    maxPrecheckAttempts: 25,

    initPrecheck: function() {
      // Don't run if already applied successfully
      if (this.precheckApplied) return;

      // Limit attempts to prevent infinite loops
      this.precheckAttempts++;
      if (this.precheckAttempts > this.maxPrecheckAttempts) return;

      const precheckIds = window.STLAddonPrecheck || [];
      if (precheckIds.length === 0) return;

      // Find all Rebuy addon items
      const items = document.querySelectorAll('[data-rebuy-id] .rebuy-addon__item');
      if (items.length === 0) {
        // Items not yet rendered, try again later
        setTimeout(() => this.initPrecheck(), 500);
        return;
      }

      // Check if Rebuy Vue instances are ready
      const rebuyWidgets = document.querySelectorAll('[data-rebuy-id]');
      let vueReady = true;
      rebuyWidgets.forEach(widget => {
        // Check if Vue has rendered content
        const content = widget.querySelector('.rebuy-widget');
        if (!content || content.hasAttribute('v-cloak')) {
          vueReady = false;
        }
      });

      if (!vueReady) {
        // Vue not ready yet, wait and retry
        setTimeout(() => this.initPrecheck(), 300);
        return;
      }

      let itemsToCheck = [];
      let allTargetsFound = true;

      // Find items that need to be prechecked
      precheckIds.forEach(precheckId => {
        let found = false;

        items.forEach(item => {
          const variantId = item.getAttribute('data-rebuy-variant-id');
          const productId = item.getAttribute('data-rebuy-product-id');

          const matches =
            (precheckId === 'v' + variantId) ||
            (precheckId === 'p' + productId);

          if (matches) {
            found = true;
            const checkbox = item.querySelector('.rebuy-checkbox, input[type="checkbox"]');
            if (checkbox && !checkbox.checked) {
              itemsToCheck.push({ checkbox, item, precheckId });
            }
          }
        });

        if (!found) allTargetsFound = false;
      });

      if (itemsToCheck.length === 0 && allTargetsFound) {
        // All items already checked AND all targets found
        this.precheckApplied = true;
        this.syncSelectedItemsFromDOM();
        setTimeout(() => this.triggerPriceUpdate(), 100);
        return;
      }

      // If not all targets found, keep trying (widgets may render later)
      if (!allTargetsFound && itemsToCheck.length === 0) {
        setTimeout(() => this.initPrecheck(), 500);
        return;
      }

      if (itemsToCheck.length > 0) {
        // Try to set via Rebuy's Vue instance if available
        const setViaVue = this.trySetViaVue(precheckIds);

        if (!setViaVue) {
          // Fallback: click checkboxes one at a time with delays
          itemsToCheck.forEach((info, index) => {
            setTimeout(() => {
              if (!info.checkbox.checked) {
                // Simulate a proper click that Vue will pick up
                info.checkbox.checked = true;
                info.checkbox.dispatchEvent(new Event('change', { bubbles: true }));
                info.checkbox.dispatchEvent(new Event('input', { bubbles: true }));
                info.item.classList.add('is-selected');

                // Also update our internal state
                const widget = info.item.closest('[data-rebuy-id]');
                const widgetId = widget ? widget.getAttribute('data-rebuy-id') : null;
                const itemData = this.getItemData(info.item, widgetId);
                const itemKey = `${widgetId}-${itemData.variantId}`;
                this.selectedItems.set(itemKey, itemData);
              }
            }, index * 100);
          });
        }

        // Verify after all clicks
        setTimeout(() => {
          this.verifyPrecheck(precheckIds);
        }, itemsToCheck.length * 100 + 500);
      }
    },

    // Re-run the default pre-check. Used after an add-to-cart (the boxes get unchecked) and on
    // bfcache restore (back button) — in both cases precheckApplied is latched true, so we reset
    // the guards before re-applying.
    reapplyPrecheck: function() {
      const precheckIds = window.STLAddonPrecheck || [];
      if (precheckIds.length === 0) return;
      this.precheckApplied = false;
      this.precheckAttempts = 0;
      this.initPrecheck();
    },

    // Try to set product.selected directly on Rebuy's Vue instance
    trySetViaVue: function(precheckIds) {
      if (typeof Rebuy === 'undefined' || !Rebuy.widgets) return false;

      let success = false;
      Rebuy.widgets.forEach(widget => {
        if (widget.products && Array.isArray(widget.products)) {
          widget.products.forEach(product => {
            const variantKey = 'v' + (product.selected_variant ? product.selected_variant.id : '');
            const productKey = 'p' + product.id;

            if (precheckIds.includes(variantKey) || precheckIds.includes(productKey)) {
              if (!product.selected) {
                product.selected = true;
                success = true;
              }
            }
          });
        }
      });

      if (success) {
        setTimeout(() => {
          this.syncSelectedItemsFromDOM();
          this.triggerPriceUpdate();
        }, 100);
      }

      return success;
    },

    // Verify precheck was successful
    verifyPrecheck: function(precheckIds) {
      const items = document.querySelectorAll('[data-rebuy-id] .rebuy-addon__item');
      let allChecked = true;
      let allTargetsFound = true;

      precheckIds.forEach(precheckId => {
        let targetFound = false;

        items.forEach(item => {
          const variantId = item.getAttribute('data-rebuy-variant-id');
          const productId = item.getAttribute('data-rebuy-product-id');

          const matches =
            (precheckId === 'v' + variantId) ||
            (precheckId === 'p' + productId);

          if (matches) {
            targetFound = true;
            const checkbox = item.querySelector('.rebuy-checkbox, input[type="checkbox"]');
            if (checkbox && !checkbox.checked) {
              allChecked = false;
            }
          }
        });

        if (!targetFound) allTargetsFound = false;
      });

      if (allChecked && allTargetsFound) {
        // Only mark as complete when ALL targets are found AND checked
        this.precheckApplied = true;
        this.syncSelectedItemsFromDOM();
        this.triggerPriceUpdate();
      } else if (this.precheckAttempts < this.maxPrecheckAttempts) {
        // Try again - either not all targets found or not all checked
        setTimeout(() => this.initPrecheck(), 500);
      }
    },

    // Sync selectedItems map from current DOM state
    syncSelectedItemsFromDOM: function() {
      this.selectedItems.clear();
      document.querySelectorAll('[data-rebuy-id] .rebuy-addon__item').forEach(item => {
        // Ignore the edit-accessories / Build-A-Bar modal's cloned widget (its own flow adds to cart).
        if (item.closest('#edit-accessories-modal')) return;
        const checkbox = item.querySelector('.rebuy-checkbox, input[type="checkbox"]');
        if (checkbox && checkbox.checked) {
          const widget = item.closest('[data-rebuy-id]');
          const widgetId = widget ? widget.getAttribute('data-rebuy-id') : null;
          const itemData = this.getItemData(item, widgetId);
          const itemKey = `${widgetId}-${itemData.variantId}`;
          this.selectedItems.set(itemKey, itemData);
        }
      });
    },

    initializeItem: function(item) {
      // Check if item has description, add class if not
      const description = item.querySelector('.rebuy-addon__item-description, .rebuy-product-description');
      if (!description || !description.textContent.trim()) {
        item.classList.add('no-description');
      }

      // Add the " xN" quantity badge + set the line price to unit x qty (and, for B2B, the
      // contextual price). Both are handled by applyContextualToItem / ensureQtyBadge.
      this.applyContextualToItem(item);
      // Currency-correct the dropdown variant up-charge from the same market-price map.
      this.applyDropdownDeltas(item);
    },

    // --- Add-on displayed price = per-buyer UNIT price x per-parent quantity ----------
    // Two adjustments to the price Rebuy renders in each widget item (the fn names keep the
    // "Contextual" prefix for history; they now cover both):
    //  1. B2B (Asana "Overall Site QA" line 30): Rebuy's feed is retail-only, so a logged-in
    //     dealer sees retail add-on prices even though the cart charges the catalog price.
    //     window.STLContextualPrices (snippets/stl-addon-contextual-prices.liquid, B2B only)
    //     maps variantId -> the buyer's UNIT price in cents.
    //  2. Quantity (Asana "STL - Data Cleanup - Accessory Quantity" 1216758773992628): some
    //     add-ons ship as a set for a given parent (e.g. a 200W siren takes 2 speakers) -> qty>1
    //     in window.STLAddonQty (custom.add_on_quantities). The displayed line price must be
    //     unit x qty; it previously showed the unit price only while the cart charged the full
    //     amount ("the price on the PDP never reflected the quantity - it did once in the cart").
    // The UNIT price is ALWAYS resolved from a STABLE source (the contextual map, else Rebuy's
    // Vue product state) -- never by reading the price span we may have already multiplied -- so
    // the rewrite is idempotent and the PDP total (main-product.liquid) stays in sync.
    formatMoneyCents: function(cents) {
      // Always format in the ACTIVE presentment/market currency. The cents we pass come from Liquid
      // variant.price (already the buyer's market price, e.g. CAD), so we must NOT route them through
      // Rebuy.Money.formatMoney — that may be configured with the shop's BASE (USD) money format and
      // would render a Canadian amount with a US format, which is exactly the inconsistency we're
      // fixing. formatPrice() uses Intl + window.Shopify.currency.active.
      return this.formatPrice(cents / 100);
    },

    // Any add-on price adjustment in play? The per-variant UNIT map (STLAddonUnitPrices) is
    // emitted for ALL shoppers (snippets/stl-addon-contextual-prices.liquid) and carries the
    // buyer's contextual/market price, so its mere presence means the displayed add-on prices
    // must be re-derived from it — this is what makes accessories/upgradeable features (qty 1,
    // non-B2B) correct in other markets, not just brackets (qty>1) and B2B.
    hasAddonPricing: function() {
      return !!window.STLAddonUnitPrices ||
        !!window.STLContextualPrices ||
        !!(window.STLAddonQty && Object.keys(window.STLAddonQty).length);
    },

    // Resolve an add-on's UNIT price (cents) from a STABLE source: the server-rendered per-variant
    // map (snippets/stl-addon-contextual-prices.liquid, Liquid variant.price -> contextual per
    // buyer) — Rebuy's widget object exposes no reliable products array, so the DOM/JS cannot derive
    // the unit itself. Never reads the displayed price span (which we may have multiplied). Returns
    // null only when the map has no entry for this variant -> leave Rebuy's rendered value.
    unitCentsStable: function(item) {
      let variantId = item.getAttribute('data-rebuy-variant-id');
      const sel = item.querySelector('select.rebuy-select, select');
      if (sel && sel.value) variantId = sel.value;
      if (!variantId) return null;
      const unitMap = window.STLAddonUnitPrices;
      if (unitMap && unitMap[variantId] != null) return unitMap[variantId];
      const ctx = window.STLContextualPrices; // B2B alias of the same map (legacy name)
      if (ctx && ctx[variantId] != null) return ctx[variantId];
      // Last-ditch: the raw unit we stashed on the node when we last multiplied its span.
      const stashed = parseInt(item.dataset && item.dataset.stlUnitCents, 10);
      return Number.isFinite(stashed) ? stashed : null;
    },

    // Keep the " xN" badge on the item title in sync with the current quantity.
    ensureQtyBadge: function(item, qty) {
      const titleEl = item.querySelector('.rebuy-addon__item-product-title');
      if (!titleEl) return;
      const existing = titleEl.querySelector('.rebuy-addon-qty');
      if (qty > 1) {
        const label = ' ×' + qty;
        if (!existing) {
          const s = document.createElement('span');
          s.className = 'rebuy-addon-qty';
          s.style.cssText = 'font-weight: 600; margin-left: 4px;';
          s.textContent = label;
          titleEl.appendChild(s);
        } else if (existing.textContent !== label) {
          existing.textContent = label;
        }
      } else if (existing) {
        existing.remove();
      }
    },

    applyContextualToItem: function(item) {
      if (!item || !item.getAttribute) return;
      // Resolve the effective variant the SAME select-aware way as unitCentsStable / the PDP
      // total / the cart, so the line, the total and the cart all key qty+price off one variant
      // (a grouping add-on can carry a different qty per variant).
      let variantId = item.getAttribute('data-rebuy-variant-id');
      const sel = item.querySelector('select.rebuy-select, select');
      if (sel && sel.value) variantId = sel.value;
      const productId = item.getAttribute('data-rebuy-product-id');
      const qty = this.accessoryQuantity(variantId, productId, item);
      this.ensureQtyBadge(item, qty);
      const priceEl = item.querySelector('.rebuy-addon__item-product-price .rebuy-money:not(.compare-at)');
      if (!priceEl) return;
      // Always re-derive the displayed price from the authoritative per-variant UNIT map
      // (snippets/stl-addon-contextual-prices.liquid, built from Liquid variant.price = the
      // buyer's contextual/MARKET price). This is what keeps add-on prices correct in other
      // markets (e.g. Canada) and for B2B, plus the unit x qty multiplier for set items. For
      // plain USD retail the map equals Rebuy's own rendered price, so this is a no-op (the
      // "=== desired" guard below also prevents any observer loop). Previously this returned
      // early for qty<=1 non-B2B items, which left accessories/upgradeable features showing
      // Rebuy's wrong market price while brackets (qty>1) were corrected.
      const unit = this.unitCentsStable(item);
      if (unit == null) return; // no map entry yet -> leave Rebuy's rendered value
      item.dataset.stlUnitCents = String(unit); // stash raw unit so totals never re-parse the xqty span
      const desired = this.formatMoneyCents(unit * qty);
      this.applyCompareAtToItem(item, variantId, qty);
      if (priceEl.textContent.trim() === desired) return; // already correct -> avoid observer loop
      priceEl.textContent = desired;
    },

    // Keep the struck-through compare-at in step with the line price (Asana 1219525455556100).
    // Rebuy renders it as ONE unit in its base-currency retail feed, so a x2 set showed e.g.
    // $199.98 next to a struck $139.99. Re-derive it as compare-at x qty from the per-variant map
    // (snippets/stl-addon-contextual-prices.liquid, Liquid compare_at_price in the buyer's market).
    // A variant with no map entry has no compare-at above the buyer's price -> hide the strike.
    // Only called once the unit price is known, so items we can't price keep Rebuy's markup.
    applyCompareAtToItem: function(item, variantId, qty) {
      const compareEl = item.querySelector('.rebuy-addon__item-product-price .rebuy-money.compare-at');
      if (!compareEl) return;
      const map = window.STLAddonUnitCompareAt;
      if (!map) return;
      const cents = variantId ? map[variantId] : null;
      if (cents == null) {
        if (compareEl.style.display !== 'none') compareEl.style.display = 'none';
        return;
      }
      if (compareEl.style.display === 'none') compareEl.style.display = '';
      const target = compareEl.querySelector('s') || compareEl;
      const desired = this.formatMoneyCents(cents * qty);
      if (target.textContent.trim() !== desired) target.textContent = desired;
    },

    // Currency-correct the "(+$X)" variant up-charge shown inside the custom variant dropdown.
    // Rebuy's Vue template renders that delta from its own retail/BASE-currency (USD) feed with a
    // hardcoded "$", so in other markets (e.g. Canada) it showed the wrong symbol AND the wrong
    // amount even though the line price above it was already corrected. Re-derive it from the SAME
    // per-variant market-price map (STLAddonUnitPrices, Liquid variant.price = the buyer's market
    // price) that drives the line price, formatted in the active market currency -- so the up-charge
    // always matches what the line price actually changes to. The template now emits these spans
    // empty; this fills them. No map entry for a variant -> leave its span empty (never a wrong
    // amount). Idempotent (writes only when the text changes) so it can't loop the observer.
    applyDropdownDeltas: function(item) {
      if (!item || !item.querySelector) return;
      const dd = item.querySelector('.rebuy-custom-dropdown');
      if (!dd) return;
      const map = window.STLAddonUnitPrices || window.STLContextualPrices;
      if (!map) return;
      const options = Array.prototype.slice.call(dd.querySelectorAll('.rebuy-custom-dropdown__option'));
      const centsOf = (vid) => (vid && map[vid] != null ? map[vid] : null);
      const priced = [];
      options.forEach((li) => { const c = centsOf(li.getAttribute('data-variant-id')); if (c != null) priced.push(c); });
      if (priced.length === 0) return; // no market prices for this dropdown -> leave Rebuy's render
      const min = Math.min.apply(null, priced);
      const label = (cents) => {
        if (cents == null) return '';
        const delta = cents - min;
        return delta > 0 ? '(+' + this.formatPrice(delta / 100) + ')' : '';
      };
      const setText = (el, txt) => { if (el && el.textContent.trim() !== txt) el.textContent = txt; };
      // Per-option up-charge
      options.forEach((li) => setText(li.querySelector('.rebuy-custom-dropdown__price-diff'), label(centsOf(li.getAttribute('data-variant-id')))));
      // Currently-selected up-charge shown on the closed trigger
      const selLi = dd.querySelector('.rebuy-custom-dropdown__option.is-selected');
      const selVid = (selLi && selLi.getAttribute('data-variant-id')) || item.getAttribute('data-rebuy-variant-id');
      setText(dd.querySelector('.rebuy-custom-dropdown__trigger .rebuy-custom-dropdown__price-diff'), label(centsOf(selVid)));
    },

    applyContextualPrices: function() {
      if (!this.hasAddonPricing()) return;
      document.querySelectorAll('[data-rebuy-id] .rebuy-addon__item').forEach((item) => {
        this.applyContextualToItem(item);
        this.applyDropdownDeltas(item);
      });
    },

    observeContextualPrices: function() {
      if (!this.hasAddonPricing()) return;
      const reapply = () => {
        clearTimeout(this._ctxTimer);
        this._ctxTimer = setTimeout(() => this.applyContextualPrices(), 30);
      };
      const observer = new MutationObserver(reapply);
      // Observe document-wide so add-on items rendered later are covered too -- notably the
      // Build-A-Bar / edit-accessories modal, which injects its own [data-rebuy-id] widget after
      // page load (Asana 1216758773992628: qty must reflect in the Build-A-Bar experience).
      // Rebuy renders prices via v-html (an innerHTML swap = childList mutation), so childList+subtree
      // already catches its re-renders; the reapply is debounced (30ms) and applyContextualToItem
      // no-ops when the text already matches, so watching our own writes can't loop.
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-rebuy-variant-id']
      });
      this.applyContextualPrices();
      // Safety re-applies for late/async widget renders (Rebuy can hydrate several seconds in).
      setTimeout(() => this.applyContextualPrices(), 800);
      setTimeout(() => this.applyContextualPrices(), 2000);
      setTimeout(() => this.applyContextualPrices(), 4000);
    },
    // --------------------------------------------------------------------------------

    handleCheckboxChange: function(checkbox) {
      const item = checkbox.closest('.rebuy-addon__item');
      const widget = checkbox.closest('[data-rebuy-id]');

      if (!item || !widget) return;

      const widgetId = widget.getAttribute('data-rebuy-id');
      const itemData = this.getItemData(item, widgetId);
      const itemKey = `${widgetId}-${itemData.variantId}`;

      if (checkbox.checked) {
        item.classList.add('is-selected');
        this.selectedItems.set(itemKey, itemData);
      } else {
        item.classList.remove('is-selected');
        this.selectedItems.delete(itemKey);
      }

      // Trigger price update with a slight delay to let Vue update
      setTimeout(() => this.triggerPriceUpdate(), 50);
    },

    handleVariantChange: function(select) {
      const item = select.closest('.rebuy-addon__item');
      const checkbox = item ? item.querySelector('.rebuy-checkbox, input[type="checkbox"]') : null;

      if (checkbox && checkbox.checked) {
        const widget = select.closest('[data-rebuy-id]');
        const widgetId = widget ? widget.getAttribute('data-rebuy-id') : null;
        const itemData = this.getItemData(item, widgetId);

        // Remove old entry and add new one
        for (const [key, value] of this.selectedItems.entries()) {
          if (key.startsWith(`${widgetId}-`) && value.productId === itemData.productId) {
            this.selectedItems.delete(key);
            break;
          }
        }

        const itemKey = `${widgetId}-${itemData.variantId}`;
        this.selectedItems.set(itemKey, itemData);

        // Trigger price update with a slight delay to let Vue update
        setTimeout(() => this.triggerPriceUpdate(), 100);
      }
    },

    getItemData: function(item, widgetId) {
      const select = item.querySelector('select.rebuy-select, select');
      const priceElement = item.querySelector('.rebuy-money.sale, .rebuy-money:not(.compare-at):not(s)');
      const titleElement = item.querySelector('.rebuy-addon__item-product-title, .rebuy-product-title');
      const link = item.querySelector('a[href*="/products/"]');

      // Get IDs from data attributes as fallback
      const dataVariantId = item.getAttribute('data-rebuy-variant-id');
      const dataProductId = item.getAttribute('data-rebuy-product-id');

      // PRIORITIZE select value - this reflects user's current selection
      let variantId = null;
      let variantTitle = '';

      if (select && select.value) {
        variantId = select.value;
        // Get the selected option's text
        const selectedOption = select.options[select.selectedIndex];
        if (selectedOption) {
          variantTitle = selectedOption.textContent.trim();
        }
      }

      // Fallback to data attribute
      if (!variantId && dataVariantId) {
        variantId = dataVariantId;
      }

      // Fallback to link
      if (!variantId && link) {
        const match = link.href.match(/variant=(\d+)/);
        variantId = match ? match[1] : null;
      }

      // Build full title with variant
      let fullTitle = titleElement ? titleElement.textContent.trim() : '';
      // Remove the quantity indicator if present
      fullTitle = fullTitle.replace(/\s*×\d+$/, '');
      if (variantTitle && variantTitle !== 'Default Title' && !fullTitle.includes(variantTitle)) {
        fullTitle = `${fullTitle} - ${variantTitle}`;
      }

      const productId = dataProductId || variantId;

      // UNIT price: prefer the stable source (the displayed span may be unit x qty now); fall
      // back to parsing the span only when no stable unit is available (then the span is the
      // un-multiplied unit). Paired with quantity below so unit x quantity = the line total.
      const unitStable = this.unitCentsStable(item);
      const unitPrice = unitStable != null
        ? unitStable / 100
        : (priceElement ? this.parsePrice(priceElement.textContent) : 0);

      return {
        variantId: variantId,
        productId: productId,
        price: unitPrice,
        title: fullTitle,
        quantity: this.accessoryQuantity(variantId, productId, item),
        widgetId: widgetId
      };
    },

    // Per-accessory quantity: some accessories ship as a set (e.g. brackets sold as a
    // pair/3-pack) and the required count is per parent->accessory line. Resolution order:
    // window.STLAddonQty (the custom.add_on_quantities metafield map rendered by
    // snippets/stl-addon-quantities.liquid, per-variant key first), then a
    // data-accessory-quantity attribute on the rendered item, then 1.
    accessoryQuantity: function(variantId, productId, item) {
      const map = window.STLAddonQty || {};
      const q = (variantId && map['v' + variantId]) || (productId && map['p' + productId]);
      if (q > 0) return q;
      const el = item && item.querySelector && item.querySelector('[data-accessory-quantity]');
      const attr = parseInt(
        (item && item.getAttribute && item.getAttribute('data-accessory-quantity')) ||
        (el && el.getAttribute('data-accessory-quantity')), 10);
      return attr > 0 ? attr : 1;
    },

    calculateTotal: function() {
      let addonsTotal = 0;
      this.selectedItems.forEach(item => {
        addonsTotal += item.price * (item.quantity || 1);
      });
      return {
        addons: addonsTotal,
        total: this.basePrice + addonsTotal
      };
    },

    updatePriceDisplay: function() {
      // Trigger the price updater in main-product.liquid
      this.triggerPriceUpdate();
    },

    // Get the main product quantity from the form
    getMainQuantity: function() {
      // Priority 1: Find quantity input inside the product form (most reliable)
      const productFormEl = document.querySelector('product-form');
      if (productFormEl) {
        const formQtyInput = productFormEl.querySelector('input[name="quantity"], .quantity__input');
        if (formQtyInput) {
          const qty = parseInt(formQtyInput.value) || 1;
          if (qty > 0) return qty;
        }
      }

      // Priority 2: Find quantity input by name attribute
      const namedQtyInput = document.querySelector('input[name="quantity"]');
      if (namedQtyInput) {
        const qty = parseInt(namedQtyInput.value) || 1;
        if (qty > 0) return qty;
      }

      // Priority 3: Find any quantity input in product info area
      const productInfo = document.querySelector('.product__info-wrapper, product-info');
      if (productInfo) {
        const infoQtyInput = productInfo.querySelector('.quantity__input');
        if (infoQtyInput) {
          const qty = parseInt(infoQtyInput.value) || 1;
          if (qty > 0) return qty;
        }
      }

      // Priority 4: General fallback
      const anyQtyInput = document.querySelector('.quantity__input');
      if (anyQtyInput) {
        return parseInt(anyQtyInput.value) || 1;
      }

      return 1;
    },

    addAllToCart: async function() {
      // Prevent double submissions
      if (this.isAddingToCart) return;
      this.isAddingToCart = true;

      // Re-sync from the live DOM so we cart the CURRENTLY-selected variant of each checked item.
      // The accessory dropdown is a custom dropdown (not a native <select>), so changing it after
      // checking the box fires only 'rebuy:variant-changed' (price display) — it never reaches
      // handleVariantChange, leaving selectedItems pinned to the check-time variant. Reading the
      // Vue-bound data-rebuy-variant-id here picks up the updated variant. (Asana 1216758773992628)
      this.syncSelectedItemsFromDOM();

      const items = [];
      const hasAccessories = this.selectedItems.size > 0;

      // Generate unique key for linking parent product with accessories
      const rebuyKey = hasAccessories
        ? `rb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        : null;

      // Find main product form - avoid Rebuy widget forms
      const productFormEl = document.querySelector('product-form');
      const productForm = productFormEl ? productFormEl.querySelector('form') : document.querySelector('form[action*="/cart/add"]');
      const mainVariantInput = productForm ? productForm.querySelector('input[name="id"]') : null;

      // Get quantity using our reliable method
      const mainQty = this.getMainQuantity();

      console.log('[RebuyCustom] Main quantity:', mainQty, 'Selected accessories:', this.selectedItems.size);

      // Native Shopify nested cart lines: a child references its parent by the parent's variant id
      // (parent_id) when both are added in the same /cart/add.js request. Emitted ALONGSIDE the
      // _rebuy_key / _parent_rebuy_key properties below — those still drive the existing cart
      // grouping, edit, and count logic, while parent_id makes the relationship structural so the
      // Shopify ORDER carries a real parent/child LineItemGroup for Fishbowl/ERP.
      const parentVariantId = (mainVariantInput && mainVariantInput.value) ? parseInt(mainVariantInput.value) : null;

      if (mainVariantInput && mainVariantInput.value) {
        const mainItem = {
          id: parseInt(mainVariantInput.value),
          quantity: mainQty
        };

        // Add linking property if accessories are selected
        if (rebuyKey) {
          mainItem.properties = {
            '_rebuy_key': rebuyKey
          };
        }

        items.push(mainItem);
      }

      // Add selected accessories/upgrades with parent linking property
      // Multiply accessory qty by main product qty (e.g., 2 products × 3 brackets = 6 brackets)
      this.selectedItems.forEach(item => {
        if (item.variantId) {
          const accItem = {
            id: parseInt(item.variantId),
            quantity: item.quantity * mainQty
          };

          // Native nested cart line: nest this add-on under the parent on the cart/checkout/order.
          if (parentVariantId) {
            accItem.parent_id = parentVariantId;
          }

          // Link item to parent product
          if (rebuyKey) {
            accItem.properties = {
              '_parent_rebuy_key': rebuyKey
            };

            // Distinguish between accessories and upgradeable features
            // Upgradeable features should NOT be removed when editing accessories
            if (item.widgetId === this.UPGRADABLE_WIDGET_ID) {
              accItem.properties['_upgradeable_feature'] = item.title || '';
            } else {
              accItem.properties['_accessory_title'] = item.title || '';
            }
          }

          items.push(accItem);
        }
      });

      if (items.length === 0) {
        console.warn('No items to add to cart');
        return;
      }

      // Show loading
      if (this.addToCartButton) {
        this.addToCartButton.setAttribute('disabled', 'disabled');
        this.addToCartButton.classList.add('loading');
      }

      // Get cart drawer and determine sections to request
      const cartDrawer = document.querySelector('cart-drawer');
      const sectionsToRender = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map(section => section.id)
        : ['cart-drawer', 'cart-icon-bubble'];

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: items,
            sections: sectionsToRender,
            sections_url: window.location.pathname
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Failed to add items');
        }

        const responseData = await response.json();

        // Clear selected items after successful add
        this.selectedItems.clear();
        this.updatePriceDisplay();

        // Uncheck all Rebuy checkboxes
        document.querySelectorAll('[data-rebuy-id] input[type="checkbox"]:checked').forEach(cb => {
          cb.checked = false;
          const item = cb.closest('.rebuy-addon__item');
          if (item) item.classList.remove('is-selected');
        });

        // Restore the default pre-checked accessories so the next add (e.g. another color of the
        // same product) keeps its included add-ons selected. Without this the boxes stay empty
        // after the first add and the shopper has to re-check them manually.
        this.reapplyPrecheck();

        // Update cart drawer using renderContents (same as light bar builder)
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          // Remove is-empty class if cart was empty
          if (cartDrawer.classList.contains('is-empty')) {
            cartDrawer.classList.remove('is-empty');
          }

          // Reset drawer state for proper animation
          cartDrawer.classList.remove('active', 'animate');

          // Small delay to ensure classes are cleared
          await new Promise(resolve => setTimeout(resolve, 50));

          // renderContents updates the drawer HTML and calls open() internally
          cartDrawer.renderContents(responseData);
        } else {
          // Fallback: go to cart page
          window.location.href = window.routes?.cart_url || '/cart';
        }

      } catch (error) {
        console.error('Error adding to cart:', error);
        alert('There was an error adding items to your cart. Please try again.');
      } finally {
        this.isAddingToCart = false;
        if (this.addToCartButton) {
          this.addToCartButton.removeAttribute('disabled');
          this.addToCartButton.classList.remove('loading');
        }
      }
    }
  };

  // Initialize
  RebuyCustom.init();

  // Expose for debugging
  window.RebuyCustom = RebuyCustom;
})();
